import { uniqueId } from 'lodash';
import React, { createContext, useContext, useState } from 'react';
import { confirm } from '@dashboard/lib/confirm';
import usePage from '@dashboard/hooks/usePage';
import { feeId, feeOptionShape } from './feeFormValues';
import { useFees } from './FeesContext';

const FeeEditContext = createContext();

export function FeeEditProvider({ children, onChange }) {
  const { page, updatePage } = usePage();
  const { state: fees, dispatch } = useFees();

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isAddingFee, setIsAddingFee] = useState(false);

  async function saveFees(nextFees) {
    try {
      const { data, error } = await updatePage.mutateAsync({ fees: nextFees });

      if (data && onChange) {
        onChange(data);
      }

      return { data, error };
    } catch (error) {
      return { error };
    }
  }

  async function onReorderFees(items) {
    const initialFees = [...fees];

    dispatch({ type: 'set', values: items });

    const savedFees = page.fees || [];
    const reorderedFees = items
      .filter(item => !item.isNew)
      .map(item => savedFees.find(fee => fee._id === item._id))
      .filter(Boolean);

    const { data, error } = await saveFees(reorderedFees);

    if (!data || error) {
      dispatch({ type: 'set', values: initialFees });
    }
  }

  async function setIsEditingFee(isEditing, id) {
    if (isAddingFee) return;

    let proceed = true;
    if (hasUnsavedChanges) {
      proceed = await confirm('Discard unsaved changes?', {
        confirm: 'Discard',
      });
    }

    if (!proceed) return;

    setHasUnsavedChanges(false);

    dispatch({
      type: 'set',
      values: fees.map(fee => ({
        ...fee,
        isEditing: feeId(fee) === id ? isEditing : false,
      })),
    });
  }

  async function onAddFee() {
    let proceed = true;

    if (hasUnsavedChanges) {
      proceed = await confirm('Discard unsaved changes?', {
        confirm: 'Discard',
      });
    }

    if (!proceed) return;

    dispatch({
      type: 'add',
      values: [{ key: uniqueId(), ...feeOptionShape, isEditing: true, isNew: true }],
    });

    setIsAddingFee(true);
    setHasUnsavedChanges(false);
  }

  return (
    <FeeEditContext.Provider
      value={{
        saveFees,
        onAddFee,
        setIsEditingFee,
        isAddingFee,
        setIsAddingFee,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        onReorderFees,
      }}
    >
      {children}
    </FeeEditContext.Provider>
  );
}

export const useFeeEdit = () => useContext(FeeEditContext);
